import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { LogOut, Users, Settings, FileText, BarChart3, AlertCircle, Globe } from 'lucide-react';
import DashboardStats from '../components/dashboard/DashboardStats';
import LeadsTab from '../components/dashboard/LeadsTab';
import FiduciariesTab from '../components/dashboard/FiduciariesTab';
import SeoTab from '../components/dashboard/SeoTab';
import UsersTab from '../components/dashboard/UsersTab';

const API = process.env.REACT_APP_BACKEND_URL;

function formatApiError(detail) {
  if (detail == null) return "Une erreur est survenue.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

export default function Dashboard() {
  const { user, logout } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [activeTab, setActiveTab] = useState('leads');
  const [leads, setLeads] = useState([]);
  const [fiduciaries, setFiduciaries] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [seoSettings, setSeoSettings] = useState(null);
  const [seoSaving, setSeoSaving] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [leadsRes, fidRes] = await Promise.all([
        axios.get(`${API}/api/leads`, { withCredentials: true }),
        axios.get(`${API}/api/fiduciaries`, { withCredentials: true }),
      ]);
      setLeads(leadsRes.data);
      setFiduciaries(fidRes.data);
      if (isAdmin) {
        const [usersRes, seoRes] = await Promise.all([
          axios.get(`${API}/api/admin/users`, { withCredentials: true }),
          axios.get(`${API}/api/site-settings`, { withCredentials: true }),
        ]);
        setUsers(usersRes.data);
        setSeoSettings(seoRes.data);
      }
    } catch (err) {
      console.error('Fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleCreateFiduciary = useCallback(async (data) => {
    setSaving(true);
    try {
      await axios.post(`${API}/api/fiduciaries`, data, { withCredentials: true });
      setMessage('Fiduciaire créé avec succès');
      fetchData();
    } catch (err) {
      setMessage(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  }, [fetchData]);

  const handleUpdateFiduciary = useCallback(async (id, data) => {
    setSaving(true);
    try {
      await axios.put(`${API}/api/fiduciaries/${id}`, data, { withCredentials: true });
      setMessage('Fiduciaire mis à jour');
      fetchData();
    } catch (err) {
      setMessage(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  }, [fetchData]);

  const handleDeleteFiduciary = useCallback(async (id) => {
    if (!window.confirm('Supprimer ce fiduciaire ?')) return;
    try {
      await axios.delete(`${API}/api/fiduciaries/${id}`, { withCredentials: true });
      setMessage('Fiduciaire supprimé');
      fetchData();
    } catch (err) {
      setMessage(formatApiError(err.response?.data?.detail));
    }
  }, [fetchData]);

  const handleAssignFiduciary = useCallback(async (userId, fiduciaryId) => {
    try {
      await axios.put(`${API}/api/admin/users/${userId}/assign`, { fiduciary_id: fiduciaryId }, { withCredentials: true });
      setMessage('Fiduciaire assigné');
      fetchData();
    } catch (err) {
      setMessage(formatApiError(err.response?.data?.detail));
    }
  }, [fetchData]);

  const handleUpdateLeadStatus = useCallback(async (leadId, status) => {
    try {
      await axios.put(`${API}/api/leads/${leadId}/status`, { status }, { withCredentials: true });
      fetchData();
    } catch (err) {
      console.error(err);
    }
  }, [fetchData]);

  const handleSaveSeo = useCallback(async () => {
    setSeoSaving(true);
    try {
      const { data } = await axios.put(`${API}/api/site-settings`, seoSettings, { withCredentials: true });
      setSeoSettings(data);
      setMessage('Paramètres SEO & LLM sauvegardés');
    } catch (err) {
      setMessage(formatApiError(err.response?.data?.detail));
    } finally {
      setSeoSaving(false);
    }
  }, [seoSettings]);

  return (
    <div className="min-h-screen bg-[#F3F4F6]" data-testid="dashboard">
      <header className="bg-white border-b-4 border-[#E3000F] sticky top-0 z-50" data-testid="dashboard-header">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-14">
            <div className="flex items-center gap-3">
              <div className="swiss-cross" />
              <span className="font-['Chivo'] font-black text-lg text-[#111827]">{"Déclaration d'impôts"}</span>
              <span className="text-xs bg-[#E3000F] text-white px-2 py-0.5 font-bold uppercase tracking-wider">{isAdmin ? 'Admin' : 'Fiduciaire'}</span>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-sm text-gray-600 font-['IBM_Plex_Sans'] hidden sm:block">{user?.email}</span>
              <button onClick={logout} className="text-gray-500 hover:text-[#E3000F] transition-colors p-2" data-testid="logout-button"><LogOut size={18} /></button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {message && (
          <div className="mb-6 border border-gray-200 bg-white p-4 flex items-center gap-2 text-sm font-['IBM_Plex_Sans']" data-testid="dashboard-message">
            <AlertCircle size={16} className="text-[#E3000F]" />
            {message}
            <button onClick={() => setMessage('')} className="ml-auto text-gray-400 hover:text-gray-600">&times;</button>
          </div>
        )}

        <DashboardStats leads={leads} />

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-white border border-gray-200 rounded-none h-auto p-1 mb-6">
            <TabsTrigger value="leads" className="rounded-none font-['IBM_Plex_Sans'] font-semibold text-sm data-[state=active]:bg-[#E3000F] data-[state=active]:text-white px-4 py-2" data-testid="tab-leads">
              <FileText size={14} className="mr-2" /> Leads
            </TabsTrigger>
            {isAdmin && (
              <TabsTrigger value="fiduciaries" className="rounded-none font-['IBM_Plex_Sans'] font-semibold text-sm data-[state=active]:bg-[#E3000F] data-[state=active]:text-white px-4 py-2" data-testid="tab-fiduciaries">
                <BarChart3 size={14} className="mr-2" /> Fiduciaires
              </TabsTrigger>
            )}
            {isAdmin && (
              <TabsTrigger value="seo" className="rounded-none font-['IBM_Plex_Sans'] font-semibold text-sm data-[state=active]:bg-[#E3000F] data-[state=active]:text-white px-4 py-2" data-testid="tab-seo">
                <Globe size={14} className="mr-2" /> SEO & LLM
              </TabsTrigger>
            )}
            <TabsTrigger value="settings" className="rounded-none font-['IBM_Plex_Sans'] font-semibold text-sm data-[state=active]:bg-[#E3000F] data-[state=active]:text-white px-4 py-2" data-testid="tab-settings">
              <Settings size={14} className="mr-2" /> Paramètres
            </TabsTrigger>
            {isAdmin && (
              <TabsTrigger value="users" className="rounded-none font-['IBM_Plex_Sans'] font-semibold text-sm data-[state=active]:bg-[#E3000F] data-[state=active]:text-white px-4 py-2" data-testid="tab-users">
                <Users size={14} className="mr-2" /> Utilisateurs
              </TabsTrigger>
            )}
          </TabsList>

          <TabsContent value="leads">
            <LeadsTab leads={leads} loading={loading} onUpdateStatus={handleUpdateLeadStatus} />
          </TabsContent>

          {isAdmin && (
            <TabsContent value="fiduciaries">
              <FiduciariesTab fiduciaries={fiduciaries} saving={saving} onCreateFiduciary={handleCreateFiduciary} onUpdateFiduciary={handleUpdateFiduciary} onDeleteFiduciary={handleDeleteFiduciary} onMessage={setMessage} />
            </TabsContent>
          )}

          {isAdmin && seoSettings && (
            <TabsContent value="seo">
              <SeoTab seoSettings={seoSettings} onSettingsChange={setSeoSettings} onSave={handleSaveSeo} saving={seoSaving} />
            </TabsContent>
          )}

          <TabsContent value="settings">
            <SettingsPanel isAdmin={isAdmin} fiduciaries={fiduciaries} />
          </TabsContent>

          {isAdmin && (
            <TabsContent value="users">
              <UsersTab users={users} fiduciaries={fiduciaries} onAssignFiduciary={handleAssignFiduciary} />
            </TabsContent>
          )}
        </Tabs>
      </div>
    </div>
  );
}

function SettingsPanel({ isAdmin, fiduciaries }) {
  if (isAdmin) {
    return (
      <div className="border border-gray-200 bg-white p-6" data-testid="settings-panel">
        <h2 className="font-['Chivo'] font-bold text-lg text-[#111827] mb-2">Paramètres</h2>
        <p className="text-sm text-gray-500 font-['IBM_Plex_Sans'] mt-4">{"En tant qu'administrateur, gérez les fiduciaires et leurs paramètres depuis l'onglet \"Fiduciaires\"."}</p>
      </div>
    );
  }

  if (fiduciaries.length > 0) {
    return (
      <div className="border border-gray-200 bg-white p-6" data-testid="settings-panel">
        <h2 className="font-['Chivo'] font-bold text-lg text-[#111827] mb-2">Paramètres</h2>
        <div className="space-y-4 mt-4">
          <p className="text-sm text-gray-500 font-['IBM_Plex_Sans']">Configurez les paramètres de votre espace fiduciaire.</p>
          {fiduciaries.map((fid) => (
            <div key={fid.id || fid.slug} className="space-y-4">
              <div>
                <Label className="block text-sm font-bold text-gray-900 mb-1">Votre page : /f/{fid.slug}</Label>
                <a href={`/f/${fid.slug}`} target="_blank" rel="noopener noreferrer" className="text-sm text-[#E3000F] hover:underline">Voir ma landing page</a>
              </div>
              <div>
                <Label className="block text-sm font-bold text-gray-900 mb-1">Clé Loomy CRM</Label>
                <Input value={fid.loomy_webhook_key || ''} disabled className="rounded-none bg-gray-50" />
                <p className="text-xs text-gray-400 mt-1">{"Contactez l'administrateur pour modifier."}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="border border-gray-200 bg-white p-6" data-testid="settings-panel">
      <h2 className="font-['Chivo'] font-bold text-lg text-[#111827] mb-2">Paramètres</h2>
      <p className="text-sm text-gray-500 font-['IBM_Plex_Sans'] mt-4">{"Aucun espace fiduciaire n'est encore assigné à votre compte. Contactez l'administrateur."}</p>
    </div>
  );
}
